// dictionary.ts — O(1) word validation, loaded once at boot.
//
// The playable set (public/dict/words.txt) is ENABLE with slurs already stripped at BUILD time
// (scripts/build-dictionary.mjs) — so a slur is not "blocked here", it simply does not exist as a
// word. Profanity (LDNOOBW) is the opposite: it stays valid and is merely FLAGGED, which the game
// turns into the deadpan "accepted, scored zero, contempt" beat. Never substring-match a denylist
// (Scunthorpe) — both checks are exact Set membership.

import { epicCount } from "./rarity";

export interface Validation {
  /** In the playable dictionary (slurs already excised). */
  valid: boolean;
  /** Valid AND on the LDNOOBW flag list — the contempt bucket, NOT a block. */
  flagged: boolean;
}

let words: Set<string> = new Set();
let profanity: Set<string> = new Set();
let ready = false;

/** Pool of valid, non-flagged words 5–10 letters — the auto-fire test-word source for the tuner. */
let firePool: string[] = [];

/** Personality test-mode pool: LONG words (8–15) with ≥2 epic letters, so you get more pairs and
 *  more rares while the common/uncommon/rare/epic ratio stays realistic (longer word = more tiles). */
let personalityPool: string[] = [];

export type DictMode = "pilot" | "full";

export interface DictMeta {
  mode: DictMode;
  words: number;
  fullWords: number;
  profanityFlagged: number;
  slursStripped: number;
}
let meta: DictMeta = { mode: "pilot", words: 0, fullWords: 0, profanityFlagged: 0, slursStripped: 0 };

/**
 * Fetch + index the dictionary. Defaults to the ~500-word PILOT set so reloads are cheap while
 * tuning; `?dict=full` loads the whole 172k ENABLE set. Call once at boot, await before validating.
 */
export async function loadDictionary(base = import.meta.env.BASE_URL): Promise<DictMeta> {
  const mode: DictMode =
    new URLSearchParams(location.search).get("dict") === "full" ? "full" : "pilot";
  const wordsFile = mode === "full" ? "words.txt" : "pilot.txt";

  const [wordsText, profJson, metaJson] = await Promise.all([
    fetch(`${base}dict/${wordsFile}`).then((r) => r.text()),
    fetch(`${base}dict/profanity.json`).then((r) => r.json()),
    fetch(`${base}dict/meta.json`).then((r) => r.json()),
  ]);

  profanity = new Set((profJson.flagged as string[]) ?? []);
  indexList(wordsText, mode);
  meta = {
    mode,
    words: words.size, // actual loaded count (pilot: ~500, full: ~172k)
    fullWords: metaJson.words ?? words.size,
    profanityFlagged: metaJson.profanityFlagged ?? profanity.size,
    slursStripped: metaJson.slursStripped ?? 0,
  };
  ready = true;
  return meta;
}

/** Build the word Set + fire pools from a newline word list. */
function indexList(wordsText: string, mode: DictMode): void {
  const list = wordsText.split("\n").filter(Boolean);
  words = new Set(list);
  firePool = list.filter((w) => w.length >= 5 && w.length <= 10 && !profanity.has(w));
  personalityPool = list.filter(
    (w) => w.length >= 7 && w.length <= 12 && !profanity.has(w) && epicCount(w) >= 2,
  );
  meta = { ...meta, mode, words: words.size };
}

/** Upgrade to the full 172k dictionary in place (no reload) — used by the left "showcase" buttons,
 *  which need the rich epic-pair word pool the pilot set is too small to provide. */
export async function ensureFullDictionary(base = import.meta.env.BASE_URL): Promise<void> {
  if (meta.mode === "full") return;
  const text = await fetch(`${base}dict/words.txt`).then((r) => r.text());
  indexList(text, "full");
}

/** O(1): is this a playable word, and is it flagged? Case/space-insensitive on the input. */
export function validate(raw: string): Validation {
  const w = raw.trim().toLowerCase();
  if (!ready) throw new Error("dictionary not loaded — await loadDictionary() first");
  const valid = words.has(w);
  return { valid, flagged: valid && profanity.has(w) };
}

/**
 * A random valid, non-flagged 5–10 letter word for the tuner's auto-fire. Uses Math.random on
 * purpose (variety while tuning) — it deliberately does NOT touch the seeded rng that drives effect
 * motion, so seeded replays stay reproducible.
 */
export function randomFireWord(): string {
  if (!firePool.length) return "compliant";
  return firePool[Math.floor(Math.random() * firePool.length)];
}

/** A long, epic-pair-rich word for lively-words mode. Falls back to the normal valid pool when
 *  the rich pool is empty (pilot mode is too small to hit the ≥2-epic filter — use ?dict=full). */
export function randomPersonalityWord(): string {
  if (!personalityPool.length) return randomFireWord();
  return personalityPool[Math.floor(Math.random() * personalityPool.length)];
}

export function hasPersonalityPool(): boolean {
  return personalityPool.length > 0;
}

export function dictReady(): boolean {
  return ready;
}
export function dictMeta(): DictMeta {
  return meta;
}
